/**
 * Small pure helpers of the timeline and the sequence viewer (0.96.0): snapping, typed timecode, the shuttle,
 * following the playhead and zooming around a point. 1.7.2: the slip and slide tools and the frames of the trim view.
 */
import {
  allClips, clipDuration, formatTimecode, framesToTicks, framesToTimecode, isDropFrameRate, layoutVideo, ops, parseTimecode, ticksPerFrame, ticksToFrames, timecodeBase,
  type Rational, type SequenceDocument,
} from '@prism/contracts';

/** Where an edge may snap to: clip edges on every track, graphics, markers and the playhead. `except` leaves a clip's own edges out. */
export function snapCandidates(doc: SequenceDocument, playhead: number, except?: string): number[] {
  const out = new Set<number>([0, playhead]);
  for (const p of layoutVideo(doc)) if (p.clip.id !== except) { out.add(p.recordIn); out.add(p.recordOut); }
  for (const { clip, track } of allClips(doc)) {
    if (track === 'video' || clip.id === except) continue;
    const c = clip as { recordInTicks: number };
    out.add(c.recordInTicks);
    out.add(c.recordInTicks + clipDuration(clip));
  }
  for (const g of doc.graphics) if (g.id !== except) { out.add(g.recordInTicks); out.add(g.recordInTicks + g.durationTicks); }
  for (const m of doc.markers ?? []) if (m.id !== except) out.add(m.atTicks);
  return [...out].sort((a, b) => a - b);
}

/**
 * Snap a move: the moving `edges` (ticks, before the move) shifted by `delta`; when one of them lands within
 * `threshold` ticks of a candidate, the delta that puts the nearest one exactly there. Returns the delta to use and
 * the candidate it snapped to (null when none was near).
 */
export function snapDelta(edges: number[], delta: number, candidates: number[], threshold: number): { delta: number; to: number | null } {
  let best: { d: number; dist: number; to: number } | null = null;
  for (const e of edges) {
    const at = e + delta;
    for (const c of candidates) {
      const dist = Math.abs(c - at);
      if (dist <= threshold && (!best || dist < best.dist)) best = { d: c - e, dist, to: c };
    }
  }
  return best ? { delta: best.d, to: best.to } : { delta, to: null };
}

/**
 * A typed timecode as a sequence position (ticks), or null. Accepts HH:MM:SS:FF (or ;FF), and digits typed as on a
 * broadcast keypad: right-aligned into HH MM SS FF, so "1000" is 00:00:10:00 and "112" is 00:00:01:12. Positions
 * are counted from 00:00:00:00, as the sequence timecode is.
 */
export function parseTimecodeEntry(text: string, rate: Rational): number | null {
  const t = text.trim();
  if (!t) return null;
  const full = parseTimecode(t, rate);
  if (full != null) return framesToTicks(full, rate);
  const parts = t.split(/[:;.]/);
  if (parts.some((p) => !/^\d*$/.test(p))) return null;
  let digits: string;
  if (parts.length > 1) digits = parts.map((p) => p.padStart(2, '0').slice(-2)).join('');
  else digits = parts[0]!;
  if (!digits || digits.length > 8) return null;
  digits = digits.padStart(8, '0');
  const [h, m, s, f] = [0, 2, 4, 6].map((i) => Number(digits.slice(i, i + 2)));
  const base = timecodeBase(rate);
  if (m! > 59 || s! > 59 || f! >= base) return null;
  const tc = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}${isDropFrameRate(rate) ? ';' : ':'}${String(f).padStart(2, '0')}`;
  const frames = parseTimecode(tc, rate);
  return frames == null ? null : framesToTicks(frames, rate);
}

/** J/K/L shuttle speeds. */
export const SHUTTLE = [1, 2, 4] as const;

/** The next shuttle rate: L speeds up forwards (1, 2, 4), J backwards (−1, −2, −4), K stops. */
export function nextShuttle(rate: number, key: 'j' | 'k' | 'l'): number {
  if (key === 'k') return 0;
  const dir = key === 'l' ? 1 : -1;
  if (Math.sign(rate) !== dir) return dir;
  return dir * Math.min(4, Math.abs(rate) * 2);
}

/**
 * Following the playhead (page-jump style): when it passes 90 % of the visible width (or is out of view), the
 * scroll position that puts it at 10 %; null when it can stay.
 */
export function followScroll(playheadX: number, scrollLeft: number, visible: number): number | null {
  if (visible <= 0) return null;
  const rel = playheadX - scrollLeft;
  if (rel >= 0 && rel <= visible * 0.9) return null;
  return Math.max(0, playheadX - visible * 0.1);
}

/** The scroll position that keeps the time under `anchorX` (pixels from the lanes' left edge) in place across a zoom. */
export function zoomScroll(anchorTicks: number, anchorX: number, nextPps: number, ticksPerSecond: number): number {
  return Math.max(0, (anchorTicks / ticksPerSecond) * nextPps - anchorX);
}

/** Pixels per second that fit `totalTicks` into `width` pixels, within the zoom's range. */
export function fitZoom(totalTicks: number, width: number, ticksPerSecond: number, min = 2, max = 240): number {
  if (totalTicks <= 0 || width <= 0) return 60;
  return Math.max(min, Math.min(max, (width * 0.95) / (totalTicks / ticksPerSecond)));
}

/* ---------------------------------------------------------------- slip and slide (1.7.2) */

/**
 * The timeline's tool (1.7.2): Select (V) moves and trims, Slip (Y) and Slide (U) act on a clip's body. 1.7.4: Ripple
 * (B) trims an edge and moves what is after it, Roll (⇧B) moves a cut between two touching clips, Blade (C) splits
 * where it is clicked (⇧: every track), Select forward (A) selects from the click to the end (⇧: that track only).
 */
export type TimelineTool = 'select' | 'slip' | 'slide' | 'ripple' | 'roll' | 'blade' | 'forward';

/** The tool a key picks (1.7.4: V Y U B ⇧B C A), or null. */
export function toolForKey(key: string, shift: boolean): TimelineTool | null {
  const k = key.toLowerCase();
  if (shift) return k === 'b' ? 'roll' : null;
  return ({ v: 'select', y: 'slip', u: 'slide', b: 'ripple', c: 'blade', a: 'forward' } as Record<string, TimelineTool>)[k] ?? null;
}

/** The track a clip is on, for the slip and slide drags. */
export type TrimTrack = 'video' | 'layer' | 'audio';

/** Where a clip is: V1, a layer or A2 (null when it is not a clip on those tracks). */
export function clipTrack(doc: SequenceDocument, id: string): TrimTrack | null {
  return allClips(doc).find((c) => c.clip.id === id)?.track ?? null;
}

/**
 * A slip or a slide by a drag of `dragTicks` (1.7.2). A slip pulls the footage under the clip like a film strip:
 * dragging right shows earlier footage (the source in point moves back), dragging left later footage. A slide moves
 * the clip with the drag. `durationOf` is a source's length by version id (0 for a still or one not known).
 */
export function trimEdit(doc: SequenceDocument, mode: 'slip' | 'slide', id: string, dragTicks: number, durationOf: (versionId: string) => number): SequenceDocument {
  if (mode === 'slide') return ops.slideClip(doc, id, dragTicks, durationOf);
  const c = allClips(doc).find((x) => x.clip.id === id)?.clip;
  return c ? ops.slipClip(doc, id, -dragTicks, durationOf(c.versionId)) : doc;
}

/** A time as a signed timecode, "+00:00:01:12" or "−00:00:00:05" (the slip offset). */
export function signedTimecode(ticks: number, rate: Rational): string {
  const tc = formatTimecode(framesToTimecode(ticksToFrames(Math.abs(ticks), rate), rate));
  return `${ticks < 0 ? '−' : '+'}${tc}`;
}

/** One frame of the trim view: a source version at a source time. */
export interface TrimFrame {
  role: 'prev-out' | 'clip-in' | 'clip-out' | 'next-in';
  versionId: string;
  /** The frame's start, in source ticks. */
  sourceTicks: number;
  label: string;
}

/**
 * The frames the trim view shows for a slip or slide (1.7.2), from the document as the drag would leave it: on V1 the
 * previous clip's last frame, the clip's first and last frames and the next clip's first frame; on a layer or A2 the
 * clip's own two. `offsetTicks` is how far the clip's source in point moved (a slip).
 */
export function trimFrames(before: SequenceDocument, after: SequenceDocument, id: string): { track: TrimTrack; offsetTicks: number; moveTicks: number; frames: TrimFrame[] } | null {
  const track = clipTrack(after, id);
  if (!track) return null;
  const tpf = ticksPerFrame(after.frameRate);
  const find = (d: SequenceDocument) => allClips(d).find((x) => x.clip.id === id)?.clip;
  const was = find(before);
  const now = find(after)!;
  const last = (c: { sourceInTicks: number; sourceOutTicks: number }) => Math.max(c.sourceInTicks, c.sourceOutTicks - tpf);
  const at = (d: SequenceDocument) => (track === 'video' ? layoutVideo(d).find((p) => p.clip.id === id)?.recordIn : (find(d) as { recordInTicks?: number } | undefined)?.recordInTicks) ?? 0;
  const offsetTicks = was ? now.sourceInTicks - was.sourceInTicks : 0;
  const moveTicks = at(after) - at(before);
  const own: TrimFrame[] = [
    { role: 'clip-in', versionId: now.versionId, sourceTicks: now.sourceInTicks, label: now.label },
    { role: 'clip-out', versionId: now.versionId, sourceTicks: last(now), label: now.label },
  ];
  if (track !== 'video') return { track, offsetTicks, moveTicks, frames: own };
  const i = after.video.findIndex((c) => c.id === id);
  const prev = after.video[i - 1];
  const next = after.video[i + 1];
  const frames: TrimFrame[] = [];
  if (prev) frames.push({ role: 'prev-out', versionId: prev.versionId, sourceTicks: last(prev), label: prev.label });
  frames.push(...own);
  if (next) frames.push({ role: 'next-in', versionId: next.versionId, sourceTicks: next.sourceInTicks, label: next.label });
  return { track, offsetTicks, moveTicks, frames };
}

/**
 * The frames the trim view shows for a roll (1.7.4): the outgoing clip's new last frame and the incoming clip's new
 * first frame, large, from the document as the roll would leave it, with how far the cut moved. `id` is the incoming
 * clip (V1, a layer or an audio track); null when it has no clip before it that touches it there.
 */
export function rollFrames(before: SequenceDocument, after: SequenceDocument, id: string): { track: TrimTrack; moveTicks: number; frames: TrimFrame[] } | null {
  const track = clipTrack(after, id);
  if (!track) return null;
  const tpf = ticksPerFrame(after.frameRate);
  const find = (d: SequenceDocument, cid: string) => allClips(d).find((x) => x.clip.id === cid)?.clip;
  const now = find(after, id);
  const was = find(before, id);
  if (!now) return null;
  const outgoing = outgoingOf(after, id);
  if (!outgoing) return null;
  const last = Math.max(outgoing.sourceInTicks, outgoing.sourceOutTicks - tpf);
  return {
    track, moveTicks: was ? now.sourceInTicks - was.sourceInTicks : 0,
    frames: [
      { role: 'prev-out', versionId: outgoing.versionId, sourceTicks: last, label: outgoing.label },
      { role: 'next-in', versionId: now.versionId, sourceTicks: now.sourceInTicks, label: now.label },
    ],
  };
}

/** The clip that ends exactly where clip `id` starts on its track (V1: the clip before it, with no gap), or null. */
export function outgoingOf(doc: SequenceDocument, id: string) {
  const placed = layoutVideo(doc);
  const i = placed.findIndex((p) => p.clip.id === id);
  if (i >= 0) {
    const p = placed[i]!, prev = placed[i - 1];
    return prev && p.gapBefore === 0 && prev.recordOut === p.recordIn ? prev.clip : null;
  }
  const all = allClips(doc);
  const me = all.find((x) => x.clip.id === id);
  if (!me || me.track === 'video') return null;
  const start = (me.clip as { recordInTicks: number }).recordInTicks;
  const same = me.track === 'layer'
    ? (doc.layers ?? []).find((l) => l.clips.some((c) => c.id === id))?.clips ?? []
    : (doc.audioTracks ?? []).find((t) => t.clips.some((c) => c.id === id))?.clips ?? doc.audio;
  return same.find((c) => c.id !== id && c.recordInTicks + clipDuration(c) === start) ?? null;
}

/** The clip that starts exactly where clip `id` ends on its track (the incoming clip of the cut after it), or null. */
export function incomingAfter(doc: SequenceDocument, id: string) {
  const placed = layoutVideo(doc);
  const i = placed.findIndex((p) => p.clip.id === id);
  if (i >= 0) {
    const p = placed[i]!, next = placed[i + 1];
    return next && next.gapBefore === 0 && next.recordIn === p.recordOut ? next.clip : null;
  }
  const all = allClips(doc);
  const me = all.find((x) => x.clip.id === id);
  if (!me || me.track === 'video') return null;
  const c0 = me.clip as { recordInTicks: number };
  const end = c0.recordInTicks + clipDuration(me.clip);
  const same = me.track === 'layer'
    ? (doc.layers ?? []).find((l) => l.clips.some((c) => c.id === id))?.clips ?? []
    : (doc.audioTracks ?? []).find((t) => t.clips.some((c) => c.id === id))?.clips ?? doc.audio;
  return same.find((c) => c.id !== id && c.recordInTicks === end) ?? null;
}

/* ---------------------------------------------------------------- ruler labels (1.7.2) */

/** The ruler's tick timecode ("00:00:00:00", 10 px mono) is about this wide, from 4 px right of its tick. */
export const RULER_TICK_LABEL_PX = 68;

/** A marker label's approximate width (10 px text, at most 140 px as in the CSS), from 8 px right of the marker. */
export function markerLabelWidth(label: string): number {
  return Math.min(140, Math.ceil(label.length * 5.6) + 6);
}

/**
 * The ruler ticks (x positions in px) whose timecode would sit under a marker's label (1.7.2): those labels are left
 * out, so a marker named at 00:00:00:00 does not overlap the first timecode. `markers` are the markers' x and label.
 */
export function rulerTicksUnderMarkers(ticks: number[], markers: Array<{ x: number; label: string }>): Set<number> {
  const spans = markers.filter((m) => m.label).map((m) => [m.x + 6, m.x + 8 + markerLabelWidth(m.label)] as const);
  const hidden = new Set<number>();
  for (const t of ticks) {
    const a = t + 4;
    const b = a + RULER_TICK_LABEL_PX;
    if (spans.some(([l, r]) => a < r && l < b)) hidden.add(t);
  }
  return hidden;
}
